export type PatternCell = {
  x?: number;
  y?: number;
  code: string;
  hex: string;
};

export type PatternLegendItem = {
  code: string;
  hex: string;
  count: number;
};

export type PatternMeta = {
  width: number;
  height: number;
  backgroundCode?: string;
  totalBeads?: number;
};

export type PatternRenderInput = {
  width: number;
  height: number;
  grid?: PatternCell[][];
  legend?: PatternLegendItem[];
  meta?: PatternMeta;
  title?: string;
};

const DEFAULT_CELL_SIZE = 18;
const SHEET_PADDING = 28;
const AXIS_SIZE = 24;
const LEGEND_GAP = 20;
const LEGEND_CARD_WIDTH = 132;
const LEGEND_CARD_HEIGHT = 28;
const LEGEND_ROW_GAP = 8;
const LEGEND_COL_GAP = 10;

function normalizeGrid(value: unknown): PatternCell[][] | undefined {
  if (!Array.isArray(value)) return undefined;
  const rows = value.filter(Array.isArray) as unknown[][];
  if (rows.length === 0) return undefined;
  return rows.map((row) =>
    row
      .filter((cell): cell is PatternCell => {
        if (!cell || typeof cell !== "object") return false;
        const candidate = cell as Partial<PatternCell>;
        return (
          typeof candidate.code === "string" &&
          typeof candidate.hex === "string"
        );
      })
      .map((cell) => ({
        x: typeof cell.x === "number" ? cell.x : undefined,
        y: typeof cell.y === "number" ? cell.y : undefined,
        code: cell.code,
        hex: cell.hex,
      })),
  );
}

function normalizeLegend(value: unknown): PatternLegendItem[] | undefined {
  if (!Array.isArray(value)) return undefined;
  const legend = value.filter((item): item is PatternLegendItem => {
    if (!item || typeof item !== "object") return false;
    const candidate = item as Partial<PatternLegendItem>;
    return (
      typeof candidate.code === "string" &&
      typeof candidate.hex === "string" &&
      typeof candidate.count === "number"
    );
  });
  return legend.length > 0 ? legend : undefined;
}

function normalizeMeta(value: unknown): PatternMeta | undefined {
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as Partial<PatternMeta>;
  if (
    typeof candidate.width !== "number" ||
    typeof candidate.height !== "number"
  ) {
    return undefined;
  }
  return {
    width: candidate.width,
    height: candidate.height,
    backgroundCode: candidate.backgroundCode,
    totalBeads: candidate.totalBeads,
  };
}

export function normalizePatternData(params: {
  width?: number | null;
  height?: number | null;
  patternGridJson?: unknown;
  patternLegendJson?: unknown;
  patternMetaJson?: unknown;
}) {
  const meta = normalizeMeta(params.patternMetaJson);
  const grid = normalizeGrid(params.patternGridJson);
  const legend = normalizeLegend(params.patternLegendJson);
  const width = meta?.width ?? params.width ?? grid?.[0]?.length ?? 0;
  const height = meta?.height ?? params.height ?? grid?.length ?? 0;

  if (!grid || width <= 0 || height <= 0) {
    return undefined;
  }

  return {
    width,
    height,
    grid,
    legend,
    meta,
  };
}

function drawCenteredText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  width: number,
  height: number,
  fontSize: number,
  color: string,
) {
  ctx.save();
  ctx.fillStyle = color;
  ctx.font = `600 ${fontSize}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText(text, x + width / 2, y + height / 2);
  ctx.restore();
}

function hexLuminance(hex: string) {
  const raw = hex.replace("#", "");
  const value =
    raw.length === 3
      ? raw
          .split("")
          .map((item) => item + item)
          .join("")
      : raw;
  const number = Number.parseInt(value.slice(0, 6), 16);
  if (Number.isNaN(number)) return 1;
  const r = (number >> 16) & 255;
  const g = (number >> 8) & 255;
  const b = number & 255;
  return (0.2126 * r + 0.7152 * g + 0.0722 * b) / 255;
}

export function renderPatternToDataUrl(input: PatternRenderInput) {
  const canvas = document.createElement("canvas");
  const cellSize = DEFAULT_CELL_SIZE;
  const gridWidth = input.width * cellSize;
  const gridHeight = input.height * cellSize;
  const sheetWidth = SHEET_PADDING * 2 + AXIS_SIZE * 2 + gridWidth;
  const legendColumns = Math.max(
    1,
    Math.floor(
      (sheetWidth - SHEET_PADDING * 2 + LEGEND_COL_GAP) /
        (LEGEND_CARD_WIDTH + LEGEND_COL_GAP),
    ),
  );
  const legendRows = Math.max(
    1,
    Math.ceil((input.legend?.length ?? 0) / legendColumns),
  );
  const legendHeight =
    legendRows * LEGEND_CARD_HEIGHT + (legendRows - 1) * LEGEND_ROW_GAP;
  const titleHeight = input.title ? 34 : 0;
  const sheetHeight =
    SHEET_PADDING * 2 +
    titleHeight +
    AXIS_SIZE * 2 +
    gridHeight +
    LEGEND_GAP +
    legendHeight;

  const scale = Math.max(2, Math.min(4, window.devicePixelRatio || 2));
  canvas.width = sheetWidth * scale;
  canvas.height = sheetHeight * scale;
  canvas.style.width = `${sheetWidth}px`;
  canvas.style.height = `${sheetHeight}px`;

  const ctx = canvas.getContext("2d");
  if (!ctx) return "";
  ctx.scale(scale, scale);
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, sheetWidth, sheetHeight);

  if (input.title) {
    ctx.fillStyle = "#111827";
    ctx.font =
      "700 20px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
    ctx.textBaseline = "top";
    ctx.fillText(input.title, SHEET_PADDING, SHEET_PADDING - 2);
  }

  const gridX = SHEET_PADDING + AXIS_SIZE;
  const gridY = SHEET_PADDING + titleHeight + AXIS_SIZE;
  const rightAxisX = gridX + gridWidth;
  const bottomAxisY = gridY + gridHeight;

  ctx.fillStyle = "#f8fafc";
  ctx.fillRect(gridX, SHEET_PADDING + titleHeight, gridWidth, AXIS_SIZE);
  ctx.fillRect(gridX, bottomAxisY, gridWidth, AXIS_SIZE);
  ctx.fillRect(SHEET_PADDING, gridY, AXIS_SIZE, gridHeight);
  ctx.fillRect(rightAxisX, gridY, AXIS_SIZE, gridHeight);

  input.grid?.forEach((row, rowIndex) => {
    row.forEach((cell, columnIndex) => {
      const x = gridX + columnIndex * cellSize;
      const y = gridY + rowIndex * cellSize;
      ctx.fillStyle = cell.hex;
      ctx.fillRect(x + 1, y + 1, cellSize - 1, cellSize - 1);
      drawCenteredText(
        ctx,
        cell.code,
        x,
        y,
        cellSize,
        cellSize,
        7,
        hexLuminance(cell.hex) > 0.62 ? "#111827" : "#ffffff",
      );
    });
  });

  ctx.lineWidth = 1;
  for (let x = 0; x <= input.width; x += 1) {
    ctx.strokeStyle = x % 10 === 0 ? "#d97706" : "#cbd5e1";
    const px = gridX + x * cellSize + 0.5;
    ctx.beginPath();
    ctx.moveTo(px, gridY);
    ctx.lineTo(px, bottomAxisY);
    ctx.stroke();
  }
  for (let y = 0; y <= input.height; y += 1) {
    ctx.strokeStyle = y % 10 === 0 ? "#d97706" : "#cbd5e1";
    const py = gridY + y * cellSize + 0.5;
    ctx.beginPath();
    ctx.moveTo(gridX, py);
    ctx.lineTo(rightAxisX, py);
    ctx.stroke();
  }

  for (let x = 0; x < input.width; x += 1) {
    const label = String(x + 1);
    const axisX = gridX + x * cellSize;
    drawCenteredText(
      ctx,
      label,
      axisX,
      SHEET_PADDING + titleHeight,
      cellSize,
      AXIS_SIZE,
      8,
      "#334155",
    );
    drawCenteredText(
      ctx,
      label,
      axisX,
      bottomAxisY,
      cellSize,
      AXIS_SIZE,
      8,
      "#334155",
    );
  }
  for (let y = 0; y < input.height; y += 1) {
    const label = String(y + 1);
    const axisY = gridY + y * cellSize;
    drawCenteredText(
      ctx,
      label,
      SHEET_PADDING,
      axisY,
      AXIS_SIZE,
      cellSize,
      8,
      "#334155",
    );
    drawCenteredText(
      ctx,
      label,
      rightAxisX,
      axisY,
      AXIS_SIZE,
      cellSize,
      8,
      "#334155",
    );
  }

  const legendY = bottomAxisY + AXIS_SIZE + LEGEND_GAP;
  input.legend?.forEach((item, index) => {
    const col = index % legendColumns;
    const row = Math.floor(index / legendColumns);
    const x = SHEET_PADDING + col * (LEGEND_CARD_WIDTH + LEGEND_COL_GAP);
    const y = legendY + row * (LEGEND_CARD_HEIGHT + LEGEND_ROW_GAP);
    ctx.strokeStyle = "#e2e8f0";
    ctx.strokeRect(x + 0.5, y + 0.5, LEGEND_CARD_WIDTH, LEGEND_CARD_HEIGHT);
    ctx.fillStyle = item.hex;
    ctx.fillRect(x + 6, y + 6, 16, 16);
    ctx.strokeStyle = "#94a3b8";
    ctx.strokeRect(x + 6.5, y + 6.5, 16, 16);
    ctx.fillStyle = "#111827";
    ctx.font =
      "600 12px system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
    ctx.textBaseline = "middle";
    ctx.fillText(
      `${item.code}  ${item.count}`,
      x + 30,
      y + LEGEND_CARD_HEIGHT / 2,
    );
  });

  return canvas.toDataURL("image/png");
}
